import React, { useCallback, useEffect, useRef, useState } from "react";
import { PhoneIncoming, PhoneOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { startRing, stopRing, primeAudioUnlock } from "@/lib/sounds";
import { toast } from "sonner";
import { CallOverlay } from "@/components/AudioCall";

/**
 * Global listener that polls `/api/voice/incoming` every ~3 seconds
 * whenever the user is signed in. If a ringing invite is found we:
 *   1. Start the repeating ringtone (Web Audio, no external asset).
 *   2. Show a full-screen "someone is calling" modal with Answer and
 *      Decline buttons.
 *   3. On Answer → mint a token via `/incoming/{id}/answer` and hand
 *      it to <CallOverlay/> to actually join the LiveKit room.
 *   4. On Decline → `/incoming/{id}/decline` and go back to idle.
 *
 * Design notes:
 *   - Polling stops while a call is active or while the tab is hidden
 *     (visibilitychange) to save battery and API calls.
 *   - We never auto-answer. Ringing MUST require an explicit tap so
 *     the browser's autoplay policy plays remote audio without asking.
 */
const POLL_INTERVAL_MS = 3000;

export default function IncomingCallListener() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [invite, setInvite] = useState(null); // ringing invite from server
  const [answered, setAnswered] = useState(null); // token payload after Answer
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const lastSeenIdRef = useRef(null);

  const poll = useCallback(async () => {
    if (!user || answered) return;
    try {
      const r = await api.get("/voice/incoming");
      const doc = r.data;
      if (doc?.id) {
        // Only kick the ring if this is a NEW invite id (avoid retrigger
        // on every poll of the same invite).
        if (lastSeenIdRef.current !== doc.id) {
          lastSeenIdRef.current = doc.id;
          startRing();
        }
        setInvite(doc);
      } else {
        // Server no longer has a ringing invite — remote may have
        // cancelled, or we just haven't received one yet.
        setInvite(null);
        stopRing();
        lastSeenIdRef.current = null;
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("voice/incoming poll error", err?.message);
      }
    }
  }, [user, answered]);

  useEffect(() => {
    if (!user) {
      stopRing();
      setInvite(null);
      setAnswered(null);
      return;
    }
    // Arm the AudioContext-unlock listener the moment the user signs
    // in, so the very next tap anywhere in the app makes the shared
    // audio pipeline ready to play the ringtone when a call arrives.
    // Without this, the FIRST incoming call plays silently on iOS.
    primeAudioUnlock();
    // Poll immediately on mount, then on interval. Pause while tab
    // hidden — a phone with the app in the background just wastes
    // battery hitting the API.
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      if (document.visibilityState === "visible") poll();
      timerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
      stopRing();
    };
  }, [user, poll]);

  const answer = async () => {
    if (!invite || busy) return;
    setBusy(true);
    stopRing();
    try {
      const r = await api.post(`/voice/incoming/${invite.id}/answer`);
      setAnswered(r.data);
      setInvite(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voiceStartError"));
      setInvite(null);
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!invite || busy) return;
    setBusy(true);
    stopRing();
    try {
      await api.post(`/voice/incoming/${invite.id}/decline`);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("decline error", err?.message);
      }
    } finally {
      setInvite(null);
      setBusy(false);
    }
  };

  const hangup = () => {
    setAnswered(null);
    lastSeenIdRef.current = null;
  };

  // ── Auto-answer from push tap ────────────────────────────────────
  // When the user taps the "Answer" action (or the notification body)
  // on an incoming-call push, the service worker deep-links them to
  // `/?call=<invite_id>&auto=1`. If the user tapped "Decline", the
  // URL is `/?call=<invite_id>&decline=1`. If the invite is still
  // ringing by the time we get here, act on it automatically so they
  // don't have to tap Answer/Decline a second time. `useRef` guard
  // makes sure a given invite is only auto-acted-on once even if the
  // poll returns it multiple times.
  const autoAnsweredRef = useRef(null);
  useEffect(() => {
    if (!invite || busy || answered) return;
    let params;
    try { params = new URLSearchParams(window.location.search); }
    catch (_) { return; }
    if (params.get("call") !== invite.id) return;
    const wantsAnswer = params.get("auto") === "1";
    const wantsDecline = params.get("decline") === "1";
    if (!wantsAnswer && !wantsDecline) return;
    if (autoAnsweredRef.current === invite.id) return;
    autoAnsweredRef.current = invite.id;
    // Strip the query so a refresh doesn't retrigger.
    try {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("auto");
      clean.searchParams.delete("call");
      clean.searchParams.delete("decline");
      window.history.replaceState({}, "", clean.toString());
    } catch (_) { /* ignore */ }
    if (wantsDecline) decline();
    else answer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite, busy, answered]);

  // Service-worker → page bridge: SW posts `{type: "navigate", url}`
  // after a notification tap on browsers whose WindowClient lacks
  // `.navigate()` (Safari). Wire it into the SPA router so a lock-
  // screen tap lands on the right chat without a full reload.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMsg = (event) => {
      const data = event.data || {};
      if (data.type !== "navigate" || !data.url) return;
      try {
        const target = new URL(data.url, window.location.origin);
        // Same-origin only.
        if (target.origin !== window.location.origin) return;
        window.history.pushState({}, "", target.pathname + target.search + target.hash);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (_) { /* ignore malformed url */ }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);

  // Active-call overlay (post-answer). Same one the caller uses so
  // both sides get identical mute / hangup / speaking UX.
  if (answered) {
    return (
      <CallOverlay
        state="connecting"
        tokenPayload={answered}
        kind="direct"
        onConnected={() => {}}
        onDisconnected={hangup}
        onError={() => hangup()}
      />
    );
  }

  if (!invite) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-stone-950/95 backdrop-blur-sm grid place-items-center px-6"
      data-testid="incoming-call-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-sm w-full text-center text-white">
        <div className="mx-auto w-20 h-20 rounded-full bg-white/10 grid place-items-center animate-pulse mb-6">
          <PhoneIncoming className="w-9 h-9" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">
          {t("voiceIncomingCall")}
        </div>
        <div
          className="font-serif-display text-3xl mt-2 truncate"
          data-testid="incoming-call-name"
        >
          {invite.from_display || t("voiceUnknownCaller")}
        </div>
        <p className="text-sm text-white/70 mt-3">{t("voiceRingingSubtitle")}</p>

        <div className="mt-10 flex items-center justify-center gap-8">
          <button
            type="button"
            onClick={decline}
            disabled={busy}
            className="flex flex-col items-center gap-2 disabled:opacity-50"
            data-testid="incoming-call-decline"
            aria-label={t("voiceDecline")}
          >
            <span className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 transition grid place-items-center shadow-lg">
              <PhoneOff className="w-7 h-7" />
            </span>
            <span className="text-xs">{t("voiceDecline")}</span>
          </button>
          <button
            type="button"
            onClick={answer}
            disabled={busy}
            className="flex flex-col items-center gap-2 disabled:opacity-50"
            data-testid="incoming-call-answer"
            aria-label={t("voiceAnswer")}
          >
            <span className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 transition grid place-items-center shadow-lg">
              <PhoneIncoming className="w-7 h-7" />
            </span>
            <span className="text-xs">{t("voiceAnswer")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
